import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import {
    Search, X, ChevronRight, LayoutDashboard, Inbox, Users, FileText, Send,
    BarChart2, Settings, CreditCard, Puzzle, MessageSquare, Zap, Building2,
    ShoppingCart, Database, Layout, ClipboardList, Instagram, Calendar,
    Megaphone, Link2, Bell, ShoppingBag, PieChart, UserPlus, Layers,
    HelpCircle, BookOpen, Contact2, Clock, MousePointer2, Ticket,
    MessageCircle, Bot, Facebook, Store, FlaskConical, Target, Linkedin,
    Video, Mic, UtensilsCrossed, History, FileClock, Globe, Plug, Sparkles,
} from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import { useOrganizationEntitlements } from '@/hooks/useFeatureLimits';
import { LIVE_CHAT_WORKSPACE_PATH } from '@/lib/constants/aiAgentsRoutes';
import { isFeatureEnabledWithFallback } from '@/lib/featureEntitlements';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AD_EXPERIMENTS_ENABLED, COMPETITOR_ADS_ENABLED } from "@/lib/deploymentEnv";

export interface SearchResult {
    id: string;
    title: string;
    description: string;
    path: string;
    icon: React.ElementType;
    keywords: string[];
    isComingSoon?: boolean;
    featureKey?: string | null;
    allowedRoles?: string[];
}

export const openSearchResult = (item: SearchResult, navigate: NavigateFunction) => {
    if (item.path.startsWith('http')) {
        window.open(item.path, '_blank', 'noopener,noreferrer');
    } else {
        navigate(item.path);
    }
};

export const searchAndRankItems = (items: SearchResult[], query: string): SearchResult[] => {
    const q = query.trim().toLowerCase();
    if (!q) return items;

    const scoreOf = (item: SearchResult): number => {
        const title = item.title.toLowerCase();
        if (title.startsWith(q)) return 0;
        if (title.includes(q)) return 1;
        if (item.description.toLowerCase().includes(q)) return 2;
        if (item.keywords.some((k) => k.toLowerCase().includes(q))) return 3;
        return -1; // no match
    };

    return items
        .map((item, index) => ({ item, score: scoreOf(item), index }))
        .filter((entry) => entry.score !== -1)
        .sort((a, b) => a.score - b.score || a.index - b.index)
        .map((entry) => entry.item);
};

export const SEARCH_ITEMS: SearchResult[] = [
    {
        id: 'overview',
        title: 'Overview',
        description: 'Global overview of your account',
        path: '/home',
        icon: LayoutDashboard,
        keywords: ['home', 'dashboard', 'stats'],
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },
    {
        id: 'inbox',
        title: 'All Messages',
        description: 'All your customer messages in one place',
        path: '/home/inbox',
        icon: Inbox,
        keywords: ['chat', 'messages', 'whatsapp', 'instagram'],
        featureKey: 'inbox',
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },
    {
        id: 'channel-whatsapp',
        title: 'WhatsApp Inbox',
        description: 'WhatsApp inbox conversations',
        path: '/home/channels/whatsapp',
        icon: MessageCircle,
        keywords: ['inbox', 'whatsapp', 'chat', 'channel'],
        featureKey: 'inbox',
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },
    {
        id: 'channel-instagram',
        title: 'Instagram Inbox',
        description: 'Instagram inbox conversations',
        path: '/home/channels/instagram',
        icon: Instagram,
        keywords: ['inbox', 'instagram', 'chat', 'channel', 'dm'],
        featureKey: 'inbox',
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },
    {
        id: 'channel-facebook',
        title: 'Facebook Inbox',
        description: 'Facebook inbox conversations',
        path: '/home/channels/facebook',
        icon: Facebook,
        keywords: ['inbox', 'facebook', 'chat', 'channel', 'messenger'],
        featureKey: 'inbox',
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },
    {
        id: 'channel-ai-chats',
        title: 'AI Chats',
        description: 'Conversations handled by your AI agents',
        path: '/home/channels/ai-agents',
        icon: Bot,
        keywords: ['inbox', 'ai', 'chat', 'channel'],
        featureKey: 'inbox',
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },
    {
        id: 'templates',
        title: 'Message Templates',
        description: 'Manage and create WhatsApp message templates',
        path: '/home/new-templates',
        icon: FileText,
        keywords: ['whatsapp', 'templates', 'hsm'],
        featureKey: 'templates',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'campaigns',
        title: 'WhatsApp Campaigns',
        description: 'Launch bulk messaging campaigns',
        path: '/home/new-campaigns',
        icon: Send,
        keywords: ['broadcast', 'marketing', 'bulk'],
        featureKey: 'campaigns',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'social-media-posts',
        title: 'Social Media Posts',
        description: 'Create, schedule, and manage Instagram, Facebook, and LinkedIn posts',
        path: '/home/social-media/posts',
        icon: Layers,
        keywords: ['social', 'posts', 'instagram', 'facebook', 'linkedin', 'all', 'schedule', 'scheduling', 'calendar'],
        allowedRoles: ['ADMIN'],
    },

    {
        id: 'instagram-posts',
        title: 'Instagram Posts',
        description: 'Manage and schedule your Instagram posts',
        path: '/home/social-media/posts?platform=instagram',
        icon: Instagram,
        keywords: ['social', 'posts', 'instagram', 'feed'],
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'facebook-posts',
        title: 'Facebook Posts',
        description: 'Manage and schedule your Facebook posts',
        path: '/home/social-media/posts?platform=facebook',
        icon: MessageCircle,
        keywords: ['social', 'posts', 'facebook', 'feed'],
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'linkedin-posts',
        title: 'LinkedIn Posts',
        description: 'Manage and schedule your LinkedIn posts',
        path: '/home/social-media/posts?platform=linkedin',
        icon: Puzzle,
        keywords: ['social', 'posts', 'linkedin', 'professional'],
        allowedRoles: ['ADMIN'],
    },

    {
        id: 'all-contacts',
        title: 'CRM',
        description: 'Manage your leads, contacts, and pipeline',
        path: '/home/boards/leads',
        icon: Users,
        keywords: ['people', 'contacts', 'crm', 'leads', 'pipeline', 'kanban'],
        featureKey: 'contacts',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'tickets',
        title: 'Tickets',
        description: 'Track and resolve support tickets',
        path: '/home/tickets',
        icon: Ticket,
        keywords: ['tickets', 'support', 'issues'],
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },
    {
        id: 'customers',
        title: 'Customers',
        description: 'Manage your CRM customers',
        path: '/home/customers',
        icon: Users,
        keywords: ['customers', 'crm', 'clients'],
        featureKey: 'contacts',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'leads',
        title: 'Leads',
        description: 'Track and manage your sales leads',
        path: '/home/leads',
        icon: UserPlus,
        keywords: ['prospects', 'sales', 'leads'],
        featureKey: 'leads',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'notes-and-tags',
        title: 'Notes and Tags',
        description: 'Organize contacts with custom labels',
        path: '/home/boards/leads?panel=tags',
        icon: ClipboardList,
        keywords: ['labels', 'notes', 'tags'],
        featureKey: 'notes_and_tags',
        allowedRoles: ['ADMIN'],
    },

    {
        id: 'whatsapp-automation',
        title: 'Automations',
        description: 'Automate WhatsApp, Instagram, and Facebook workflows',
        path: '/home/automation',
        icon: Zap,
        keywords: ['auto', 'bot', 'replies', 'automation', 'whatsapp', 'instagram', 'facebook'],
        featureKey: 'automations',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'whatsapp-flows',
        title: 'Forms',
        description: 'Create interactive shopping and booking flows',
        path: '/home/flows',
        icon: Layout,
        keywords: ['flows', 'forms', 'interactive', 'whatsapp forms'],
        featureKey: 'whatsapp_flows',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'form-data',
        title: 'Form Submissions',
        description: 'View data from WhatsApp forms',
        path: '/home/flows?view=submissions',
        icon: Database,
        keywords: ['data', 'export', 'responses', 'forms'],
        featureKey: 'whatsapp_flows',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'instagram-automation',
        title: 'Instagram Automations',
        description: 'Automate Instagram interactions',
        path: '/home/automation/instagram',
        icon: Instagram,
        keywords: ['ig', 'bot', 'auto', 'automation'],
        featureKey: 'instagram_automation',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'instagram-insights',
        title: 'Instagram Insights',
        description: 'Reach, engagement, and audience analytics for Instagram',
        path: '/home/instagram/insights',
        icon: BarChart2,
        keywords: ['ig', 'analytics', 'stats', 'insights'],
        featureKey: 'instagram_automation',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'instagram-automation-leads',
        title: 'Automation Leads',
        description: 'Leads captured by Instagram automation flows',
        path: '/home/instagram/automation/leads',
        icon: UserPlus,
        keywords: ['ig', 'leads', 'automation'],
        featureKey: 'instagram_automation',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'instagram-orders',
        title: 'Instagram Orders',
        description: 'Orders placed through Instagram',
        path: '/home/instagram/orders',
        icon: ShoppingBag,
        keywords: ['ig', 'orders', 'commerce'],
        featureKey: 'instagram_automation',
        allowedRoles: ['ADMIN'],
    },

    {
        id: 'meta-ads',
        title: 'Meta Ads',
        description: 'Manage your Meta advertising campaigns',
        path: '/home/ads',
        icon: Megaphone,
        keywords: ['facebook', 'instagram', 'ads'],
        featureKey: 'meta_ads',
        allowedRoles: ['ADMIN'],
    },
    ...(AD_EXPERIMENTS_ENABLED ? [{
        id: 'ads-experiments',
        title: 'Ad Experiments',
        description: 'A/B test your Meta ad campaigns',
        path: '/home/ads/experiments',
        icon: FlaskConical,
        keywords: ['ads', 'ab test', 'experiments', 'split test'],
        featureKey: 'meta_ads',
        allowedRoles: ['ADMIN'],
    } as SearchResult] : []),
    ...(COMPETITOR_ADS_ENABLED ? [{
        id: 'competitor-ads',
        title: 'Competitor Ads',
        description: 'See what ads your competitors are running',
        path: '/home/competitor-ads',
        icon: Target,
        keywords: ['ads', 'competitor', 'spy', 'research'],
        allowedRoles: ['ADMIN'],
    } as SearchResult] : []),
    {
        id: 'linkedin-ads',
        title: 'LinkedIn Ads',
        description: 'Manage your LinkedIn advertising campaigns',
        path: '/home/linkedin-ads',
        icon: Linkedin,
        keywords: ['ads', 'linkedin', 'b2b'],
        featureKey: 'linkedin_ads',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'tiktok-ads',
        title: 'TikTok Ads',
        description: 'Manage your TikTok advertising campaigns',
        path: '/home/tiktok-ads',
        icon: Video,
        keywords: ['ads', 'tiktok'],
        featureKey: 'tiktok_ads',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'google-ads',
        title: 'Google Ads',
        description: 'Manage your Google advertising campaigns',
        path: '/home/google-ads',
        icon: Search,
        keywords: ['ads', 'google', 'search ads'],
        featureKey: 'google_ads',
        allowedRoles: ['ADMIN'],
    },

    {
        id: 'scheduling-overview',
        title: 'Scheduling Overview',
        description: 'Overview of your appointments and slots',
        path: '/home/scheduling-overview',
        icon: Clock,
        keywords: ['appointments', 'slots', 'overview'],
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'meeting-links',
        title: 'Meetings',
        description: 'Create and manage meeting invitation links',
        path: '/home/meeting-links',
        icon: Link2,
        keywords: ['links', 'meetings', 'invite'],
        featureKey: 'meeting_links',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'scheduling-contacts',
        title: 'Scheduling Contacts',
        description: 'Contacts captured through scheduling and bookings',
        path: '/home/scheduling-contacts',
        icon: Contact2,
        keywords: ['booking', 'appointments', 'contacts'],
        featureKey: 'appointments',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'time-slots',
        title: 'Availability Slots',
        description: 'Manage available booking slots',
        path: '/home/time-slots',
        icon: MousePointer2,
        keywords: ['booking', 'slots', 'available'],
        featureKey: 'time_slots',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'scheduling-integrations',
        title: 'Calendar Integrations',
        description: 'Connect with Calendar apps',
        path: '/home/scheduling-integrations',
        icon: Puzzle,
        keywords: ['booking', 'sync', 'calendar'],
        featureKey: 'integrations',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'scheduling',
        title: 'Scheduling',
        description: 'Manage your booking types and schedules',
        path: '/home/scheduling',
        icon: Calendar,
        keywords: ['booking', 'schedule', 'appointments'],
        featureKey: 'scheduling',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'whatsapp-reminders',
        title: 'WhatsApp Reminders',
        description: 'Send automated reminders on WhatsApp',
        path: '/home/whatsapp-reminders',
        icon: Bell,
        keywords: ['reminders', 'alerts', 'notifications'],
        featureKey: 'whatsapp_reminders',
        allowedRoles: ['ADMIN'],
    },

    {
        id: 'commerce-overview',
        title: 'Commerce Overview',
        description: 'Commerce dashboard — orders, revenue, and store performance',
        path: '/home/commerce/dashboard',
        icon: LayoutDashboard,
        keywords: ['commerce', 'store', 'dashboard'],
        featureKey: 'commerce',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'products',
        title: 'Product Catalog',
        description: 'Manage your product catalog',
        path: '/home/commerce/products',
        icon: ShoppingCart,
        keywords: ['catalog', 'items', 'store'],
        featureKey: 'products',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'orders',
        title: 'Orders',
        description: 'Manage your store orders',
        path: '/home/orders',
        icon: ShoppingBag,
        keywords: ['sales', 'orders', 'ecommerce'],
        featureKey: 'orders',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'abonded-carts',
        title: 'Abandoned Carts',
        description: 'Recover abandoned shopping carts',
        path: '/home/abonded-carts',
        icon: ShoppingBag,
        keywords: ['recovery', 'carts', 'sales'],
        featureKey: 'abonded_carts',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'payments',
        title: 'Payments',
        description: 'Manage transactions and payouts',
        path: '/home/payments',
        icon: CreditCard,
        keywords: ['money', 'transactions', 'payouts'],
        featureKey: 'payments',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'commerce-invoices',
        title: 'Invoices',
        description: 'Generate and manage order invoices',
        path: '/home/invoices',
        icon: FileText,
        keywords: ['commerce', 'invoices', 'billing document'],
        featureKey: 'invoices',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'store-reports',
        title: 'Commerce Reports',
        description: 'View ecommerce performance reports',
        path: '/home/store-reports',
        icon: BarChart2,
        keywords: ['analytics', 'reports', 'ecommerce'],
        featureKey: 'store_reports',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'commerce-v2',
        title: 'Commerce V2',
        description: 'Products, orders, categories, and abandoned carts in one workspace',
        path: '/home/commerce/v2',
        icon: Store,
        keywords: ['commerce', 'store', 'v2', 'products', 'orders'],
        featureKey: 'commerce',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'data-store',
        title: 'Data Store',
        description: 'Structured data collected from forms and flows',
        path: '/home/data-stores',
        icon: Database,
        keywords: ['data', 'forms', 'store'],
        featureKey: 'data_store',
        allowedRoles: ['ADMIN'],
    },

    {
        id: 'analytics',
        title: 'Analytics',
        description: 'Main reporting dashboard',
        path: '/home/analytics',
        icon: PieChart,
        keywords: ['reports', 'stats', 'data', 'campaign', 'performance'],
        featureKey: 'analytics',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'message-reports',
        title: 'Message Reports',
        description: 'Detailed analysis of message delivery',
        path: '/home/message-reports',
        icon: FileText,
        keywords: ['delivery', 'reports', 'stats'],
        featureKey: 'message_reports',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'ads-results',
        title: 'Ads Results',
        description: 'View results from your Meta ads',
        path: '/home/ads',
        icon: Megaphone,
        keywords: ['roi', 'ads', 'results'],
        featureKey: 'meta_ads',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'sales-and-conversions',
        title: 'Sales and Conversions',
        description: 'Track your shop sales and conversions',
        path: '/home/sales-and-conversions',
        icon: ShoppingBag,
        keywords: ['conversions', 'sales', 'revenue'],
        featureKey: 'sales_and_conversions',
        allowedRoles: ['ADMIN'],
    },

    {
        id: 'settings',
        title: 'Settings',
        description: 'Manage your account settings',
        path: '/home/account-settings',
        icon: Settings,
        keywords: ['account', 'profile', 'config'],
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },
    {
        id: 'workspace-settings',
        title: 'Workspace settings',
        description: 'Workspace profile, brand websites, WhatsApp accounts, MCP and AI knowledge',
        path: '/home/workspace-settings',
        icon: Globe,
        keywords: ['workspace', 'profile', 'brand', 'websites', 'settings', 'mcp', 'whatsapp', 'knowledge'],
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'team-management',
        title: 'Team Management',
        description: 'Manage your team members',
        path: '/home/team-management',
        icon: Users,
        keywords: ['team', 'members', 'permissions'],
        featureKey: 'team_management',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'team-chat',
        title: 'Team Chat',
        description: 'Internal chat with your team',
        path: '/home/team-chat',
        icon: MessageSquare,
        keywords: ['team', 'chat', 'internal'],
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },
    {
        id: 'audit-log',
        title: 'Audit Log',
        description: 'Review account activity and changes',
        path: '/home/audit-log',
        icon: FileClock,
        keywords: ['audit', 'log', 'history', 'activity'],
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'integrations',
        title: 'Integrations',
        description: 'Connect with third-party apps',
        path: '/home/integrations',
        icon: Puzzle,
        keywords: ['apps', 'api', 'connect'],
        featureKey: 'integrations',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'billing',
        title: 'Billing',
        description: 'Manage your subscription and invoices',
        path: '/home/account-settings?section=billing',
        icon: CreditCard,
        keywords: ['payment', 'subscription', 'plan'],
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'plans',
        title: 'Plans & Subscriptions',
        description: 'Compare and switch subscription plans',
        path: '/home/account-settings?section=plans',
        icon: Send,
        keywords: ['plans', 'subscription', 'upgrade', 'pricing'],
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'business-accounts',
        title: 'WhatsApp Business',
        description: 'Manage your WhatsApp Business account',
        path: '/home/integrations/whatsapp-business',
        icon: Building2,
        keywords: ['whatsapp', 'waba', 'accounts', 'business accounts', 'integrations'],
        featureKey: 'business_accounts',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'mcp',
        title: 'MCP',
        description: 'Connect WeNext to Claude, Cursor and other MCP clients',
        path: '/home/workspace-settings?section=mcp',
        icon: Plug,
        keywords: ['mcp', 'claude', 'cursor', 'model context protocol'],
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'ai-knowledge',
        title: 'AI Knowledge',
        description: 'Tell the AI about this workspace so it can answer customers',
        path: '/home/workspace-settings?section=ai-knowledge',
        icon: Sparkles,
        keywords: ['ai', 'knowledge', 'context', 'organization'],
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'commerce-settings',
        title: 'Commerce Settings',
        description: 'Manage Meta commerce settings',
        path: '/home/commerce/products?tab=settings',
        icon: ShoppingCart,
        keywords: ['shop', 'meta', 'commerce'],
        featureKey: 'commerce',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'social-accounts',
        title: 'Social Accounts',
        description: 'Connect your social media profiles',
        path: '/home/integrations?tab=social',
        icon: Instagram,
        keywords: ['connect', 'social', 'profiles'],
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'ai-agents',
        title: 'AI Agents',
        description: 'Deploy AI assistants to handle queries',
        path: '/home/ai-agents',
        icon: Bot,
        keywords: ['ai', 'bot', 'assistant'],
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'whatsapp-ai-agent',
        title: 'WhatsApp AI Agent',
        description: 'Configure the AI agent that handles WhatsApp conversations',
        path: '/home/ai-agents?type=whatsapp',
        icon: Bot,
        keywords: ['ai', 'whatsapp', 'agent', 'assistant'],
        featureKey: 'ai_generation',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'voice-agents',
        title: 'Voice Agents',
        description: 'Configure AI agents that handle phone calls',
        path: '/home/ai-agents?type=voice',
        icon: Mic,
        keywords: ['ai', 'voice', 'calls', 'phone', 'agent'],
        featureKey: 'ai_calling',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'live-chat',
        title: 'Live Chat',
        description: 'Real-time customer support channels',
        path: LIVE_CHAT_WORKSPACE_PATH,
        icon: MessageSquare,
        keywords: ['support', 'chat', 'live'],
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'notifications',
        title: 'Notifications',
        description: 'View your recent activity and alerts',
        path: '/home/notifications',
        icon: Bell,
        keywords: ['alerts', 'updates', 'unread'],
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },

    {
        id: 'petpooja-overview',
        title: 'Petpooja Overview & Sync',
        description: 'Connect and sync your Petpooja POS menu',
        path: '/home/petpooja',
        icon: UtensilsCrossed,
        keywords: ['petpooja', 'pos', 'menu', 'restaurant', 'sync'],
        featureKey: 'petpooja',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'petpooja-orders',
        title: 'Petpooja Order History',
        description: 'Orders placed through Petpooja',
        path: '/home/petpooja/orders',
        icon: History,
        keywords: ['petpooja', 'orders', 'restaurant'],
        featureKey: 'petpooja',
        allowedRoles: ['ADMIN'],
    },

    {
        id: 'whats-new',
        title: 'Updates',
        description: 'Release notes and product updates',
        path: '/home/updates',
        icon: Megaphone,
        keywords: ['release', 'notes', 'changelog', 'updates', 'new'],
        featureKey: 'whats_new',
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },
    {
        id: 'how-to-use',
        title: 'How to Use',
        description: 'Getting started guides and tutorials',
        path: '/home/how-to-use',
        icon: HelpCircle,
        keywords: ['guides', 'tutorials', 'start'],
        featureKey: 'how_to_use',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'help-articles',
        title: 'Help Articles',
        description: 'Browse our help documentation',
        path: '/home/help-articles',
        icon: BookOpen,
        keywords: ['documentation', 'articles', 'help'],
        featureKey: 'help_articles',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'contact-support',
        title: 'Contact Support',
        description: 'Chat with our support team on WhatsApp',
        path: 'https://wa.me/918977232350',
        icon: Contact2,
        keywords: ['chat', 'call', 'support', 'whatsapp'],
        featureKey: 'contact_support',
        allowedRoles: ['ADMIN'],
    },
];

export const useAvailableSearchItems = (): SearchResult[] => {
    const { user } = useAuth();
    const { data: entitlements } = useOrganizationEntitlements();

    const roleRaw = (user?.role || '').toString().toUpperCase();
    const isSuperAdmin = !!(user as any)?.is_super_admin;
    const isOwner = !!(user as any)?.is_organization_owner;
    const isAdmin = roleRaw === 'ADMIN' || isSuperAdmin || isOwner;
    const currentRole = isAdmin ? 'ADMIN' : (roleRaw === 'AGENT' ? 'AGENT' : 'USER');

    const features = entitlements?.entitlements?.features;

    return React.useMemo(() => {
        const enabledFeatures = features || {};
        return SEARCH_ITEMS.filter(item => {
            if (item.allowedRoles && !item.allowedRoles.includes(currentRole)) {
                return false;
            }

            if (item.featureKey) {
                const alwaysSearchable = ['integrations', 'billing', 'whats_new', 'how_to_use', 'help_articles', 'raise_ticket', 'contact_support'];
                if (alwaysSearchable.includes(item.featureKey)) return true;

                return isFeatureEnabledWithFallback(enabledFeatures, item.featureKey);
            }

            return true;
        });
    }, [currentRole, features]);
};

interface GlobalSearchProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ open, onOpenChange }) => {
    const [query, setQuery] = useState('');
    const navigate = useNavigate();

    const availableItems = useAvailableSearchItems();

    const [results, setResults] = useState<SearchResult[]>(availableItems);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (open) {
            setQuery('');
            setResults(availableItems);
            setSelectedIndex(0);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [open, availableItems]);

    useEffect(() => {
        if (!query) {
            setResults(availableItems);
            return;
        }

        setResults(searchAndRankItems(availableItems, query));
        setSelectedIndex(0);
    }, [query, availableItems]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((prev) => (prev + 1) % results.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (results[selectedIndex]) {
                handleSelect(results[selectedIndex]);
            }
        } else if (e.key === 'Escape') {
            onOpenChange(false);
        }
    };

    const handleSelect = (item: SearchResult) => {
        if (item.isComingSoon) return;
        openSearchResult(item, navigate);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="p-0 gap-0 max-w-2xl bg-background/95 backdrop-blur-xl border-border shadow-2xl overflow-hidden [&>button]:hidden duration-200">
                <DialogTitle className="sr-only">Global Search</DialogTitle>
                <div className="flex flex-col h-[60vh] md:h-[600px]">
                    <div className="flex items-center px-4 py-4 border-b border-border bg-muted/20">
                        <Search className="w-5 h-5 text-muted-foreground mr-3" />
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Search for pages..."
                            className="flex-1 bg-transparent border-none outline-none text-lg placeholder:text-muted-foreground/50 text-foreground"
                        />
                        <button
                            onClick={() => onOpenChange(false)}
                            className="p-1 hover:bg-muted rounded-md transition-colors"
                        >
                            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                                <span className="text-xs">ESC</span>
                            </kbd>
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-2 scrollbar-thin">
                        {results.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                                <Search className="w-12 h-12 mb-4 opacity-20" />
                                <p>No results found for "{query}"</p>
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {results.map((item, index) => (
                                    <div
                                        key={item.id}
                                        onClick={() => handleSelect(item)}
                                        onMouseEnter={() => setSelectedIndex(index)}
                                        className={cn(
                                            "flex items-center px-4 py-3 rounded-lg cursor-pointer transition-all duration-200 group relative",
                                            selectedIndex === index
                                                ? "bg-primary/10"
                                                : "hover:bg-muted/50",
                                            item.isComingSoon && "opacity-60 cursor-not-allowed"
                                        )}
                                    >
                                        <div className={cn(
                                            "p-2 rounded-md mr-4 transition-colors",
                                            selectedIndex === index ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                                        )}>
                                            <item.icon className="w-5 h-5" />
                                        </div>

                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <h4 className={cn(
                                                    "font-medium transition-colors",
                                                    selectedIndex === index ? "text-primary" : "text-foreground"
                                                )}>
                                                    {item.title}
                                                </h4>
                                                {item.isComingSoon && (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-semibold uppercase tracking-wider">
                                                        Soon
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                                                {item.description}
                                            </p>
                                        </div>

                                        {!item.isComingSoon && (
                                            <ChevronRight className={cn(
                                                "w-5 h-5 transition-all duration-200",
                                                selectedIndex === index
                                                    ? "opacity-100 text-primary translate-x-0"
                                                    : "opacity-0 -translate-x-2"
                                            )} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="px-4 py-3 border-t border-border bg-muted/20 flex items-center justify-between text-[10px] text-muted-foreground">
                        <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                                <kbd className="rounded border px-1 bg-background">↑↓</kbd> to navigate
                            </span>
                            <span className="flex items-center gap-1">
                                <kbd className="rounded border px-1 bg-background">Enter</kbd> to select
                            </span>
                        </div>
                        <div>
                            {results.length} result{results.length !== 1 ? 's' : ''}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
